// src/admin/hooks/useWorkflow.js
import { useState, useEffect, useCallback, useRef } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { devError } from '../../shared/utils/clientLog';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { DEFAULT_START_STEP } from '../utils/workflowStepFactory';
import { saveFlowLayout } from '../utils/workflowFlowLayoutStorage';
import { sanitizeWorkflow } from '../utils/workflowSanitize';
import { API_URL } from '../../config/env';

/**
 * What a save against a published workflow is about to do, in words, before it does it.
 *
 * Editing a published workflow never touches it: the backend forks a draft
 * (`Workflow#update_draft`) and the editor moves onto that draft, swapping the id in the URL.
 * That used to happen in silence, behind a "Saved just now" that cleared itself after three
 * seconds -- so an admin could change a published workflow's scope, be told it was saved, and
 * have changed nothing that was live.
 *
 * Asked rather than announced, because the published version is running: a draft the admin did
 * not mean to create is a pending version somebody else has to understand later. The editor can
 * discard one now (see `discardDraft`), but the cheaper answer is still not to create it.
 */
function draftForkPrompt(workflow) {
  const joining = workflow?.pending_draft?.id;
  const whatHappens = joining
    ? `Your changes will go to the existing draft (#${joining}), not to the published version.`
    : 'Saving will create a new draft. The published version will not change.';

  return `"${workflow?.name || 'This workflow'}" is published.\n\n${whatHappens}\n\n`
    + 'The published version stays live until you publish the draft.\n\nContinue?';
}

/**
 * What a discard is about to cost, in words, before it does it.
 *
 * Two drafts look alike in the editor and are not. A draft that replaces a published version
 * holds nothing but pending changes: discarding it drops those and the published version stays
 * live, untouched. An orphan draft -- `replaces` absent, never published -- has no earlier
 * version to fall back on, so the same button removes the workflow outright. Same action, very
 * different consequence, so the confirmation has to say which one this is.
 */
function discardPrompt(workflow) {
  const name = workflow?.name || 'this workflow';
  const publishedId = workflow?.replaces?.id;

  if (publishedId) {
    return `Discard the draft of "${name}"?\n\n`
      + 'The changes in this draft are lost. Workflow '
      + `#${publishedId} stays live and is not touched.\n\nThis cannot be undone.`;
  }

  return `Delete "${name}"?\n\n`
    + 'This draft has never been published, so there is no earlier version to fall back on — '
    + 'the workflow is removed entirely.\n\nThis cannot be undone.';
}

export default function useWorkflow() {
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const params = useParams();
  const [workflow, setWorkflowState] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  // What just happened to the save, for the standing bar in the editor. `saveStatus` cannot
  // carry any of it: it is a green success word that clears itself after three seconds, which
  // is how the fork stayed invisible. Shapes: { kind: 'forked', publishedId } after a save
  // moved the editor onto a new draft, { kind: 'declined' } after the admin backed out of one,
  // { kind: 'error', messages } when the save was refused — which used to reach the admin as
  // nothing whatsoever, only a console line.
  const [draftNotice, setDraftNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const workflowRef = useRef(workflow);
  workflowRef.current = workflow;

  // On mount, try localStorage or fallback
  useEffect(() => {
    const localData = window.localStorage.getItem('workflowJSON');
    if (localData !== null && localData !== undefined) {
      try {
        setWorkflowState(sanitizeWorkflow(JSON.parse(localData)));
      } catch (err) {
        if (!isMounted.current) return;
        console.warn('Failed to parse workflow from localStorage:', err);
      }
    }
  }, [isMounted]);

  const setWorkflow = useCallback((newWorkflow) => {
    const sanitized = newWorkflow ? sanitizeWorkflow(newWorkflow) : null;
    setWorkflowState(sanitized);
    if (sanitized) {
      window.localStorage.setItem('workflowJSON', JSON.stringify(sanitized));
    } else {
      window.localStorage.removeItem('workflowJSON');
    }
  }, []);

  async function createNewWorkflow(setNodes, setEdges, formData) {
    const newWorkflow = {
      name: formData.name || '',
      description: formData.description || '',
      instruction: formData.instruction || '',
      self_startable: formData.self_startable || false,
      user_workflow: formData.user_workflow || false,
      number_of_people: formData.number_of_people || null,
      expected_work_duration: formData.expected_work_duration || null,
      required_tools: formData.required_tools || '',
      required_parts: formData.required_parts || '',
      relevant_to: formData.relevant_to || null,
      tag_ids: formData.tag_ids || [],
      workflow_steps: [{ ...DEFAULT_START_STEP }],
    };

    const apiUrl = `${API_URL}/v1/admin/workflows`;

    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ workflow: newWorkflow }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      devError('Error creating workflow:', errorData.errors);
      throw new Error('Failed to create workflow');
    }

    const savedWorkflow = await response.json();
    setWorkflow(savedWorkflow);
    if (savedWorkflow.id) {
      saveFlowLayout({
        rootId: savedWorkflow.root_workflow_id ?? savedWorkflow.id,
        workflowId: savedWorkflow.id,
        version: savedWorkflow.version,
        positionsById: { start: { x: 250, y: 150 } },
        workflowSteps: savedWorkflow.workflow_steps,
      });
    }
    setNodes([]);
    setEdges([]);
    return savedWorkflow;
  }

  const loadWorkflowById = useCallback((workflowId, setNodes, setEdges) => {
    if (!workflowId) return;
    const apiUrl = `${API_URL}/v1/admin/workflows/${workflowId}`;
    setNodes([]);
    setEdges([]);
    setLoading(true);
    fetchWithAuth(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        setWorkflow(sanitizeWorkflow({ ...data }));
      })
      .catch((err) => console.error(`Error loading workflow with ID "${workflowId}":`, err))
      .finally(() => setLoading(false));
  }, [setWorkflow]);

  function saveWorkflow(workflowToSave = null) {
    const currentWorkflow = workflowToSave || workflow;
    if (!currentWorkflow) {
      return Promise.resolve();
    }

    const apiUrl = `${API_URL}/v1/admin/workflows`;

    const isNewWorkflow = !currentWorkflow.id;

    const workflowData = sanitizeWorkflow({
      name: currentWorkflow.name,
      self_startable: currentWorkflow.self_startable,
      user_workflow: currentWorkflow.user_workflow,
      description: currentWorkflow.description,
      instruction: currentWorkflow.instruction,
      number_of_people: currentWorkflow.number_of_people,
      expected_work_duration: currentWorkflow.expected_work_duration,
      required_tools: currentWorkflow.required_tools,
      required_parts: currentWorkflow.required_parts,
      workflow_steps: currentWorkflow.workflow_steps,
      relevant_to: currentWorkflow.relevant_to,
      tag_ids: currentWorkflow.tag_ids,
    });

    const requestOptions = {
      method: isNewWorkflow ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        workflow: workflowData,
      }),
    };

    const url = isNewWorkflow ? apiUrl : `${apiUrl}/${currentWorkflow.id}`;

    // Every save path arrives here -- the settings modal, the step panel, the toolbar, and
    // deleting a node or an edge on the canvas -- so this is the one place the fork can be
    // caught before it happens.
    const wouldForkADraft = !isNewWorkflow && Boolean(currentWorkflow.state)
      && currentWorkflow.state !== 'draft';

    if (wouldForkADraft && !window.confirm(draftForkPrompt(currentWorkflow))) {
      // The edit stays on screen: every caller applies it locally before saving, and throwing
      // the admin's work away is a worse answer than leaving it unsaved. Nothing claims it was
      // saved, and the next save asks again.
      setDraftNotice({ kind: 'declined', publishedId: currentWorkflow.id });
      return Promise.resolve(null);
    }

    return fetchWithAuth(url, requestOptions)
      .then((response) => {
        if (!response.ok) {
          return response.json().catch(() => ({})).then((errorData) => {
            devError('Error saving workflow:', errorData.errors || errorData.error);
            const msg =
              (Array.isArray(errorData.errors) && errorData.errors.join('; ')) ||
              errorData.error ||
              response.statusText ||
              'Failed to save workflow';
            const err = new Error(msg);
            err.errors = Array.isArray(errorData.errors) ? errorData.errors : [msg];
            throw err;
          });
        }
        return response.json();
      })
      .then((data) => {
        setWorkflow(data);
        setDraftNotice((prev) => (prev?.kind === 'error' ? null : prev));
        setSaveStatus('Saved just now');
        setTimeout(() => setSaveStatus(''), 3000);

        // A different id back from the server means the save forked a draft. This comparison
        // was already here, quietly swapping the URL; now it also says so.
        const routeFlowId = params.flow_id;
        if (
          !isNewWorkflow &&
          data?.id &&
          routeFlowId &&
          String(data.id) !== String(routeFlowId)
        ) {
          setDraftNotice({ kind: 'forked', publishedId: routeFlowId });
          navigate(`/admin/flow/${data.id}/edit`, { replace: true });
        }
      })
      .catch((error) => {
        console.error('Error during saveWorkflow:', error);
        setDraftNotice({
          kind: 'error',
          messages: error?.errors?.length ? error.errors : [error?.message || 'The save failed.'],
        });
        throw error;
      });
  }

  function downloadWorkflow() {
    if (!workflow) return;
    const dataStr = JSON.stringify(workflow, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'workflow.json';
    link.click();
    URL.revokeObjectURL(url);
  }

  function uploadWorkflow(jsonData) {
    if (!workflow) {
      console.error('No workflow is currently open.');
      return;
    }
    const allowedFields = ['self_startable', 'workflow_steps'];
    const updatedWorkflow = { ...workflow };

    Object.keys(jsonData).forEach((key) => {
      if (allowedFields.includes(key)) {
        updatedWorkflow[key] = jsonData[key];
      }
    });
    setWorkflow(updatedWorkflow);
  }

  const validateWorkflow = useCallback(async (workflowToValidate = null, { mode = 'publish' } = {}) => {
    const w = workflowToValidate || workflowRef.current;
    if (!w?.id) {
      return { valid: false, details: [], contract_version: null, normalization_notes: [] };
    }
    const url = `${API_URL}/v1/admin/workflows/${w.id}/validate`;
    const response = await fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workflow_steps: w.workflow_steps,
        mode,
      }),
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.error || 'Validation request failed');
    }
    return response.json();
  }, []);

  async function publishWorkflow(workflowToPublish = null) {
    const w = workflowToPublish || workflow;
    if (!w?.id) {
      return;
    }
    const url = `${API_URL}/v1/admin/workflows/${w.id}/publish`;
    const response = await fetchWithAuth(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const msg =
        (Array.isArray(errBody.errors) && errBody.errors.join('; ')) ||
        errBody.error ||
        response.statusText ||
        'Publish failed';
      const err = new Error(msg);
      err.errors = Array.isArray(errBody.errors) ? errBody.errors : [msg];
      err.details = Array.isArray(errBody.details) ? errBody.details : [];
      err.contractVersion = errBody.contract_version;
      throw err;
    }
    const data = await response.json();
    setWorkflow(data);
    setDraftNotice(null);
    setSaveStatus('Published');
    setTimeout(() => setSaveStatus(''), 4000);
  }

  /**
   * Discard the open draft: the only exit from a draft other than publishing it.
   *
   * A hard delete on the backend, refused there rather than attempted when something still
   * references the row, so a blocked discard arrives as a reason and not as a 500. The editor's
   * job is to confirm by name, say which of the two kinds of draft this is, and then leave --
   * carrying on in an editor whose workflow no longer exists is the one outcome to avoid.
   *
   * @returns {Promise<boolean>} true when the draft was discarded; false when the admin backed
   *   out or the backend refused.
   */
  async function discardDraft(workflowToDiscard = null) {
    const w = workflowToDiscard || workflow;
    if (!w?.id || w.state !== 'draft') return false;
    if (!window.confirm(discardPrompt(w))) return false;

    const url = `${API_URL}/v1/admin/workflows/${w.id}`;
    let response;
    try {
      response = await fetchWithAuth(url, { method: 'DELETE' });
    } catch (error) {
      devError('Error discarding draft:', error);
      setDraftNotice({
        kind: 'discard_failed',
        messages: [error?.message || 'The draft could not be discarded.'],
      });
      return false;
    }

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const messages = Array.isArray(errBody.errors) && errBody.errors.length
        ? errBody.errors
        : [errBody.error || response.statusText || 'The draft could not be discarded.'];
      devError('Error discarding draft:', messages);
      setDraftNotice({ kind: 'discard_failed', messages });
      return false;
    }

    // The open workflow is mirrored into localStorage and read back on the next mount, so a
    // discarded draft would otherwise reappear on a workflow that no longer exists.
    setWorkflow(null);
    setDraftNotice(null);

    const publishedId = w.replaces?.id;
    navigate(publishedId ? `/admin/flow/${publishedId}/edit` : '/admin/flows/', { replace: true });
    return true;
  }

  return {
    workflow,
    setWorkflow,
    saveStatus,
    draftNotice,
    dismissDraftNotice: () => setDraftNotice(null),
    loading,
    createNewWorkflow,
    loadWorkflowById,
    saveWorkflow,
    downloadWorkflow,
    uploadWorkflow,
    publishWorkflow,
    validateWorkflow,
    discardDraft,
  };
}
